import { useQuery } from 'react-query'

const { REACT_APP_PANOPTICON_CAN_SEND_BASE } = process.env

export interface PermissionEventSummary {
  success: boolean
  senderNumber: string
  recipientNumber?: string
  timeRange: {
    start: string
    end: string
  }
  totalEvents: number
  allowedCount: number
  blockedCount: number
  blockRate: number
  avgResponseRate: number
  avgUnrepliedCount: number
  topBlockReasons: Array<{
    reason: string
    count: number
    percentage: number
  }>
  graceUsage: {
    averageUsed: number
    averageLeft: number
  }
}

const fetchPermissionEventsSummary = async (
  senderNumber: string,
  startDate: string,
  endDate: string,
  recipientNumber?: string
): Promise<PermissionEventSummary> => {
  const encodedSender = encodeURIComponent(senderNumber)

  // Make end date inclusive by adding time to end of day
  const inclusiveEndDate = new Date(`${endDate}T23:59:59.999Z`).toISOString()

  const params = new URLSearchParams({
    startDate,
    endDate: inclusiveEndDate,
  })

  if (recipientNumber) {
    params.append('recipientNumber', recipientNumber)
  }

  const url = `${REACT_APP_PANOPTICON_CAN_SEND_BASE}/permission-events/senders/${encodedSender}/summary?${params}`

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

export const usePermissionEventsSummary = (
  senderNumber: string,
  startDate: string,
  endDate: string,
  recipientNumber?: string,
  enabled = true
) => {
  return useQuery(
    [
      'permission-events-summary',
      senderNumber,
      startDate,
      endDate,
      recipientNumber,
    ],
    () =>
      fetchPermissionEventsSummary(
        senderNumber,
        startDate,
        endDate,
        recipientNumber
      ),
    {
      enabled: enabled && !!senderNumber && !!startDate && !!endDate,
      staleTime: 60000, // 60 seconds
      refetchOnWindowFocus: false,
    }
  )
}

export default usePermissionEventsSummary
